import type { Draft } from 'immer';
import produce from 'immer';
import type { Reducer } from 'redux';

import type { ContractingState, ContractingStatusFailureResponse } from '@makemydeal/dr-dash-types';
import { isArray, isObject, isPlainObject } from 'lodash';
import { nanoid } from 'nanoid';
import {
    CONTRACTING_CANCEL_FAILURE_EVENT,
    CONTRACTING_CLOSE_MODAL,
    CONTRACTING_EVENT_RECEIVED,
    CONTRACTING_FS_RESPONSE_RECEIVED,
    CONTRACTING_GENERATE_CLICKED,
    CONTRACTING_IN_PROGRESS,
    CONTRACTING_OFFER_SAVE_FAILURE_EVENT,
    CONTRACTING_OFFER_SAVE_SUCCESS_EVENT,
    CONTRACTING_OPEN_MODAL,
    CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS,
    CONTRACTING_SAVED_WITH_ERRORS_EVENT,
    CONTRACTING_SET_FORM_CONTRACT_TYPE,
    CONTRACTING_STAGE_CANCEL_FAILURE,
    CONTRACTING_STAGE_CANCEL_REQUEST,
    CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_CANCEL_SUCCESS,
    CONTRACTING_STAGE_COPY_AWAITING_CANCEL,
    CONTRACTING_STAGE_COPY_FAILURE,
    CONTRACTING_STAGE_COPY_REQUEST,
    CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_COPY_SUCCESS,
    COPY_CREDIT_APP_DEAL_UPDATE_REQUEST,
    CONTRACTING_STAGE_START_FAILURE,
    CONTRACTING_STAGE_START_REQUEST,
    CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_START_SUCCESS,
    CONTRACTING_STAGE_VERIFY_FAILURE,
    CONTRACTING_STAGE_VERIFY_REQUEST,
    CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_VERIFY_SUCCESS,
    CONTRACTING_START_FAILURE_EVENT,
    CONTRACTING_TIMEOUT_FAILURE,
    CONTRACTING_VERIFY_FAILURE_EVENT,
    CONTRACTING_VERIFY_SUCCESS_EVENT,
    CONTRACTING_WAITING_FOR_FS,
    SET_CONTRACT_VALIDATION_LOADING,
    CONTRACTING_STATUS_SUCCESS,
    CONTRACTING_STATUS_FAILURE,
    SET_CONTRACT_VALIDATION_ERRORS,
    SET_FIRST_CONTRACT_API_ERRORED_VPP
} from '../actionTypes/contractingActionTypes';
import { OFFER_SAVE_REQUEST } from '../actionTypes/offerActionTypes';
import { normalizeContractErrors, parseEventTime, flattenLeaf } from '../utils/contractingUtils';

export type ContractingReducer = Reducer<ContractingState>;

export const initialState: ContractingState = {
    failureCurrentState: undefined,
    failureEvent: undefined,
    failureReasons: undefined,
    isInProgress: false,
    modalOpen: false,
    stage: undefined,
    startTimeMs: undefined,
    status: undefined,
    successEvent: undefined,
    correlationId: undefined,
    cancelled: false,
    formContractType: undefined,
    saveOfferRequestEndTimeMs: undefined,
    saveOfferRequestStartTimeMs: undefined,
    cancelRequestEndTimeMs: undefined,
    cancelRequestStartTimeMs: undefined,
    cancelEventTimeMs: undefined,
    cancelEventReceivedTimeMs: undefined,
    cancelEventToEventBrokerDelayMs: undefined,
    cancelEventBrokerReceivedTimeMs: undefined,
    cancelEventTimeStamp: undefined,
    startRequestEndTimeMs: undefined,
    startRequestStartTimeMs: undefined,
    startEventTimeMs: undefined,
    startEventReceivedTimeMs: undefined,
    startEventToEventBrokerDelayMs: undefined,
    startEventBrokerReceivedTimeMs: undefined,
    startEventTimeStamp: undefined,
    verifyRequestEndTimeMs: undefined,
    verifyRequestStartTimeMs: undefined,
    verifyEventTimeMs: undefined,
    verifyEventReceivedTimeMs: undefined,
    verifyEventToEventBrokerDelayMs: undefined,
    verifyEventBrokerReceivedTimeMs: undefined,
    verifyEventTimeStamp: undefined,
    fieldValidationErrors: undefined,
    fieldValidationErrorFlags: undefined,
    firstContractApiErroredVPP: undefined,
    waitingForFsResponse: false,
    fsEventReceivedTimeMs: undefined,
    fsEventDelayMs: undefined,
    contractValidationLoading: false,
    contractingStatusInfo: undefined,
    contractingStatusFailure: undefined,
    eventName: undefined,
    eventPayload: undefined,
    copyStage: 'idle',
    copyAttempts: 0,
    copyInFlightSince: null,
    lastStageTimestampMs: undefined
};

/**
 * This function extracts the error reasons from the error structure returned by the storefront API.
 */
export const getStageRequestErrorReasons = (action: any) => {
    const errors = action.payload?.response?.errors;
    if (!errors || !isArray(errors) || !errors.length) {
        return undefined;
    }

    const reasons = [];
    for (const error of errors) {
        const properties = error?.properties;
        if (!properties?.length) {
            continue;
        }

        for (const prop of properties) {
            if (prop?.message) {
                reasons.push(prop.message);
            }
        }
    }

    return reasons;
};

export const getErrorReasons = (action: any) => {
    const errors = action.payload?.payload?.message?.errors;
    if (!errors || !errors.length) {
        return undefined;
    }

    const reasons = [];
    for (const error of errors) {
        if (error?.message) {
            reasons.push(error.message);
        }
    }

    return reasons;
};

/**
 * Maps contract error response objects to an array of human-readable error reason strings.
 *
 * @param errors - The error object returned from the contract API, which may contain nested error details.
 * @returns An array of formatted error reason strings extracted from the error object.
 */

export const getValidationErrorReasons = (action: any) => {
    const errors = action.payload?.payload?.message?.deal_errors;
    if (!errors || Object.keys(errors).length === 0) {
        return getErrorReasons(action);
    }
    const reasons: string[] = [];
    Object.keys(errors).forEach((key) => {
        const errorReasons = normalizeContractErrors(errors[key]);
        reasons.push(...errorReasons);
    });
    return reasons;
};

//TODO: Need to check other reasons and update the error messages in a declarative way
export const findRequiredLenderFieldErrorReasons = (errors: any): string[] => {
    const INSURANCE_TYPE_ROAD_HAZARD_KEY = 'product_data.insurance_data[1].insurance_type.roadhazard';
    const INSURANCE_TYPE_GAP_KEY = 'product_data.insurance_data[1].insurance_type.gap';
    const TAX_TYPE_KEY = 'product_data.tax_data.tax_type';
    const FEE_TYPE_KEY = 'product_data.fee_data.fee_type';
    const PAYMENT_TYPE_KEY = 'product_data.payment';
    const RESIDUAL_VALUE_KEY = 'product_data.residual_value';
    const PAID_TO_FEE_TYPE_REGEX = /^product_data\.fee_data(\[\d+\])?\./;
    const INSURANCE_DATA_REGEX = /^product_data\.insurance_data(\[\d+\])?\./;
    const APPLICANT_DATA_REGEX = /^applicant_data\[\d+\]$/;

    if (!errors) {
        return [];
    }

    const requiredFields = errors['missing_required_lender_fields'] || {};
    const displayKeys = errors['missing_required_lender_fields_display_keys'] || {};

    const productDataErrors = requiredFields['product_data'] || [];
    const roadHazardErrors = requiredFields[INSURANCE_TYPE_ROAD_HAZARD_KEY] || [];
    const gapErrors = requiredFields[INSURANCE_TYPE_GAP_KEY] || [];
    const feeTypeErrors = requiredFields[FEE_TYPE_KEY] || [];
    const paymentErrors = requiredFields[PAYMENT_TYPE_KEY] || [];
    const paidToFeeTypeErrors = Object.keys(requiredFields).filter(
        (feeTypeKey) => feeTypeKey.match(PAID_TO_FEE_TYPE_REGEX) && feeTypeKey !== FEE_TYPE_KEY
    );
    const insuranceDataErrors = Object.keys(requiredFields).filter(
        (key) => key.match(INSURANCE_DATA_REGEX) && key !== INSURANCE_TYPE_ROAD_HAZARD_KEY && key !== INSURANCE_TYPE_GAP_KEY
    );
    const residualValueErrors = requiredFields[RESIDUAL_VALUE_KEY] || [];
    const applicantDataErrors = Object.keys(requiredFields).filter((key) => key.match(APPLICANT_DATA_REGEX));

    const errorMessages: string[] = [];

    const requiredType = requiredFields[TAX_TYPE_KEY] || [];
    if (requiredType.some((type: any) => type === 'florida_document_stamp_tax')) {
        errorMessages.push('FL Doc Stamp Tax required for Buyers in the state of Florida');
    }
    if (Array.isArray(productDataErrors) && productDataErrors.includes('rate_program')) {
        errorMessages.push('Please go to Dealertrack and Select the Rate Program');
    }
    if (Array.isArray(roadHazardErrors) && roadHazardErrors.includes('type_choices')) {
        errorMessages.push('Please go to Dealertrack and Select the Program Type for Road Hazard');
    }
    if (Array.isArray(gapErrors) && gapErrors.includes('type_choices')) {
        errorMessages.push('Please go to Dealertrack and Select the Program Type for GAP');
    }
    if (Array.isArray(feeTypeErrors) && feeTypeErrors.length > 0) {
        feeTypeErrors.forEach((field: string) => {
            if (field === 'acquisition_fee_amount') {
                errorMessages.push('Missing required lender field: Acquisition Fee is required.');
            } else {
                errorMessages.push(`Missing required lender field: ${field} is required.`);
            }
        });
    }
    if (Array.isArray(paymentErrors) && paymentErrors.length > 0) {
        ['base_money_factor', 'money_factor'].forEach((field) => {
            if (paymentErrors.includes(field)) {
                errorMessages.push(`Missing required lender field: ${field} is required.`);
            }
        });
    }
    if (Array.isArray(paidToFeeTypeErrors) && paidToFeeTypeErrors.length > 0) {
        paidToFeeTypeErrors.forEach((field) => {
            const extractedFeeType = field.replace(PAID_TO_FEE_TYPE_REGEX, '');
            errorMessages.push(`Missing required lender field: ${extractedFeeType} is required.`);
        });
    }

    if (Array.isArray(residualValueErrors) && residualValueErrors.length > 0) {
        const residualDisplayKeys = displayKeys[RESIDUAL_VALUE_KEY] || [];
        residualValueErrors.forEach((field: string) => {
            const displayKeyEntry = residualDisplayKeys.find((dk: { field_name: string }) => dk.field_name === field);
            const displayField = displayKeyEntry?.display_key || field;
            errorMessages.push(`Missing required lender field: ${displayField} is required.`);
        });
    }

    insuranceDataErrors.forEach((key) => {
        const missingFields = requiredFields[key];
        const keyDisplayKeys = displayKeys[key] || [];

        if (Array.isArray(missingFields)) {
            missingFields.forEach((field: string) => {
                const displayKeyEntry = keyDisplayKeys.find((dk: { field_name: string }) => dk.field_name === field);
                const displayField = displayKeyEntry?.display_key || field;
                errorMessages.push(`Missing required lender field: ${displayField} is required.`);
            });
        }
    });

    applicantDataErrors.forEach((key) => {
        const missingFields = requiredFields[key];
        const keyDisplayKeys = displayKeys[key] || [];

        if (Array.isArray(missingFields)) {
            missingFields.forEach((field: string) => {
                const displayKeyEntry = keyDisplayKeys.find((dk: { field_name: string }) => dk.field_name === field);
                const displayKey = displayKeyEntry?.display_key;

                if (field === 'signor_title' && displayKey === 'buyer.business_signor_title') {
                    errorMessages.push('Missing required lender field: Signer Title is required.');
                }
            });
        }
    });
    return errorMessages;
};

/**
 * Converts snake_case fee name to Title Case with "Fee" suffix.
 * e.g., "vehicle_inspection" → "Vehicle Inspection Fee"
 * e.g., "processing_fee" → "Processing Fee" (no duplicate suffix)
 */
const humanizeFee = (name: string): string => {
    const titleCase = name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    // Avoid "Fee Fee" - only add suffix if not already ending with "Fee"
    return titleCase.endsWith('Fee') ? titleCase : `${titleCase} Fee`;
};

/**
 * Formats DealXG field paths to human-readable display names.
 * Transforms technical paths like "product_data.fee_data[2].fee_type.vehicle_inspection.paid_to"
 * into user-friendly names like "Vehicle Inspection Fee".
 */
export const formatLenderFieldPath = (path: string): string => {
    // Pattern 1: FEE_TYPE - product_data.fee_data[N].fee_type.<fee_name>.* OR product_data.fee_data.fee_type.<fee_name>.* → "<Fee Name> Fee"
    const feeMatch = path.match(/^product_data\.fee_data(\[\d+\])?\.fee_type\.([^.]+)/);
    if (feeMatch) {
        return humanizeFee(feeMatch[2]);
    }

    // Pattern 2: VEHICLE_INSURANCE - dealStructure.vehicles.sellingVehicle.vehicleInsurance.<field> → "ins.<field>"
    const vehicleInsuranceMatch = path.match(/^dealStructure\.vehicles\.sellingVehicle\.vehicleInsurance\.(.+)$/);
    if (vehicleInsuranceMatch) {
        return `ins.${vehicleInsuranceMatch[1]}`;
    }

    // Pattern 3: DEALFEE - dealfee.<fee_name> → "<Fee Name> Fee"
    const dealfeeMatch = path.match(/^dealfee\.(.+)$/);
    if (dealfeeMatch) {
        return humanizeFee(dealfeeMatch[1]);
    }

    // Pattern 4: BUSINESS_SIGNER - dealStructure.participants.business.businessSigner.signerTitle → "Signer Title"
    const businessSignerMatch = path.match(/^dealStructure\.participants\.business\.businessSigner\.signerTitle$/);
    if (businessSignerMatch) {
        return 'Signer Title';
    }

    // Default: return original path
    return path;
};

export const PRODUCT_TYPE_SELECTION_MESSAGES: Record<string, string> = {
    'ins.roadhazard_type': 'Please go to Dealertrack and Select the Program Type for Road Hazard',
    'ins.gap_type': 'Please go to Dealertrack and Select the Program Type for GAP'
};

const INSURANCE_TYPE_PATH_REGEX = /^product_data\.insurance_data(?:\[\d+\])?\.insurance_type\.(?:gap|roadhazard)$/;

/**
 * Extracts lender field errors from SavedSuccessfully event payload.
 * Transforms DealXG paths to human-readable display names.
 * This is separate from findRequiredLenderFieldErrorReasons which handles FNI paths with special business logic.
 */
export const getSavedSuccessfullyEventLenderFieldErrors = (errors: any): string[] => {
    if (!errors || typeof errors !== 'object') {
        return [];
    }

    const errorMessages: string[] = [];
    const lenderFieldProperties = ['missing_required_lender_fields', 'missing_required_lender_fields_not_mapped'];

    for (const propName of lenderFieldProperties) {
        const fieldsObj = errors[propName];
        if (isObject(fieldsObj)) {
            for (const [path, messages] of Object.entries(fieldsObj)) {
                if (INSURANCE_TYPE_PATH_REGEX.test(path)) {
                    continue;
                }
                if (isArray(messages)) {
                    messages.forEach((msg: string) => {
                        if (PRODUCT_TYPE_SELECTION_MESSAGES[path]) {
                            errorMessages.push(PRODUCT_TYPE_SELECTION_MESSAGES[path]);
                            return;
                        }
                        const displayValue = msg === 'Missing Required Field' ? formatLenderFieldPath(path) : msg;
                        errorMessages.push(`Missing required lender field: ${displayValue} is required.`);
                    });
                }
            }
        }
    }

    return errorMessages;
};

/**
 * Extracts the category label from a charge count key.
 * Strips '_count' and '_charges', replaces '_' with space, appends 'charges'.
 * e.g. 'upfront_count' → 'upfront charges', 'other_charges_count' → 'other charges'
 */
const formatChargeCategory = (key: string): string => {
    const label = key
        .replace(/_count$/, '')
        .replace(/_charges/, ' charges')
        .replace(/_/g, ' ');
    return label.includes('charges') ? label : `${label} charges`;
};

/**
 * Extracts errors from the charge_count_exceeds_max structure.
 * Produces human-readable messages with dynamic removal count and display keys.
 */
export const getChargeCountExceedsMaxErrors = (errors: any): string[] => {
    const chargeCountErrors = errors?.charge_count_exceeds_max;
    if (!chargeCountErrors || typeof chargeCountErrors !== 'object') {
        return [];
    }

    const errorMessages: string[] = [];

    for (const [key, value] of Object.entries(chargeCountErrors)) {
        const entry = value as { max_values?: number; values?: string[]; display_key?: string[] };
        const { max_values, values, display_key } = entry;

        if (!isArray(values) || !values.length || typeof max_values !== 'number') {
            continue;
        }

        const excessCount = values.length - max_values;
        if (excessCount <= 0) {
            continue;
        }

        const category = formatChargeCategory(key);
        const displayNames = isArray(display_key) && display_key.length > 0 ? display_key.join(', ') : values.join(', ');

        errorMessages.push(
            `There are more than allowable ${category} entered, remove ${excessCount} of the following: (${displayNames})`
        );
    }

    return errorMessages;
};
/**
 * Formats a "Field required" error using dealxg_path from properties when available.
 */
export const formatFieldRequiredError = (err: any): string[] => {
    if (!err) return [];
    const dealxgPaths: string[] = err.properties?.dealxg_path;
    if (Array.isArray(dealxgPaths) && dealxgPaths.length > 0) {
        return dealxgPaths.map((dealxgPath) => `${dealxgPath}: ${err.message}`);
    }
    return [err.message];
};

/**
 *  Extract verification failure reasons from the error structure
 */
export const getVerificationFailureReasons = (eventPayload: any): string[] => {
    const reasonsSet: Set<string> = new Set();
    if (eventPayload?.error_reasons) {
        eventPayload.error_reasons.forEach((err: any[]) => {
            if (err?.[0]?.reason) {
                reasonsSet.add(err[0].reason);
            }
        });
    }
    if (Array.isArray(eventPayload?.message?.errors)) {
        eventPayload.message.errors.forEach((err: any) => {
            if (!err?.message) return;
            if (typeof err.message === 'string' && err.message.toLowerCase() === 'field required') {
                formatFieldRequiredError(err).forEach((reason) => reasonsSet.add(reason));
            } else {
                reasonsSet.add(err.message);
            }
        });
    }
    if (Array.isArray(eventPayload?.message?.validation_errors)) {
        eventPayload.message.validation_errors.forEach((err: any) => {
            if (err?.message) {
                reasonsSet.add(err.message);
            }
        });
    }
    if (Array.isArray(eventPayload?.message?.errors?.validation_errors)) {
        eventPayload.message.errors.validation_errors.forEach((err: any) => {
            if (err?.message) {
                reasonsSet.add(err.message);
            }
        });
    }

    if (eventPayload?.message?.errors?.validations || eventPayload?.message?.errors?.complexvalidations) {
        const allValidations = {
            ...eventPayload.message.errors.validations,
            ...eventPayload.message.errors.complexvalidations
        };
        for (const [key, value] of Object.entries(allValidations)) {
            reasonsSet.add(`${key}: ${value}`);
        }
    }

    const errors = eventPayload?.message?.errors;
    if (errors && !Array.isArray(errors) && !Array.isArray(errors.validation_errors)) {
        const reasons = findRequiredLenderFieldErrorReasons(errors);
        reasons.forEach((reason) => reasonsSet.add(reason));
    }

    // Charge count exceeds max errors (available for all event types)
    if (errors && typeof errors === 'object') {
        const chargeCountErrors = getChargeCountExceedsMaxErrors(errors);
        chargeCountErrors.forEach((reason) => reasonsSet.add(reason));
    }

    // New categories from SavedSuccessfully event
    if (errors && typeof errors === 'object') {
        // Extract validation errors from multiple error properties with array message format
        const validationErrorProperties = [
            'validation_errors',
            'validation_errors_not_mapped',
            'validations_not_mapped',
            'validations'
        ];

        for (const propName of validationErrorProperties) {
            const errorObj = errors[propName];
            if (isPlainObject(errorObj)) {
                for (const [path, messages] of Object.entries(errorObj)) {
                    if (isArray(messages)) {
                        messages.forEach((msg: string) => {
                            reasonsSet.add(`${path}: ${msg}`);
                        });
                    }
                }
            }
        }

        // Missing Lender Fields (DealXG paths) - at the end like findRequiredLenderFieldErrorReasons
        const lenderFieldErrors = getSavedSuccessfullyEventLenderFieldErrors(errors);
        lenderFieldErrors.forEach((reason) => reasonsSet.add(reason));
    }

    return Array.from(reasonsSet);
};

export const reducer = (state: ContractingState = initialState, action: any) =>
    produce(state, (draft: Draft<ContractingState>) => {
        switch (action.type) {
            case OFFER_SAVE_REQUEST: {
                if (action.meta?.source === CONTRACTING_IN_PROGRESS) {
                    draft.stage = 'saveOffer';
                    draft.saveOfferRequestStartTimeMs = Date.now();
                }
                break;
            }

            case CONTRACTING_CANCEL_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = action.payload.payload?.message?.current_state;
                draft.failureReasons = undefined;
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_START_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                if (action.meta?.isContractErrorMapping) {
                    draft.failureReasons = getValidationErrorReasons(action);
                } else {
                    draft.failureReasons = getErrorReasons(action);
                }
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = [];
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_VERIFY_SUCCESS_EVENT: {
                if (action.payload.eventName === 'ContractingDecisions:Verified') {
                    draft.status = 'success';
                } else if (action.payload.eventName === 'ContractingDecisions:VerifiedWithWarnings') {
                    draft.status = 'warning';
                }
                draft.successEvent = action.type;
                draft.failureCurrentState = undefined;
                draft.failureEvent = undefined;
                draft.failureReasons = undefined;

                break;
            }

            case CONTRACTING_VERIFY_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.successEvent = undefined;

                const reasons = getVerificationFailureReasons(action.payload.payload);

                draft.failureReasons = reasons.length ? reasons : undefined;

                break;
            }

            case CONTRACTING_SAVED_WITH_ERRORS_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.successEvent = undefined;

                const reasons = getVerificationFailureReasons(action.payload.payload);

                draft.failureReasons = reasons.length ? reasons : undefined;

                break;
            }

            case CONTRACTING_OFFER_SAVE_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = undefined;
                draft.successEvent = undefined;
                draft.saveOfferRequestEndTimeMs = action.payload.timestampMs;

                break;
            }

            case CONTRACTING_OFFER_SAVE_SUCCESS_EVENT: {
                draft.saveOfferRequestEndTimeMs = action.payload.timestampMs;

                break;
            }

            case CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = getStageRequestErrorReasons(action);
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = getStageRequestErrorReasons(action);
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = getStageRequestErrorReasons(action);
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = getStageRequestErrorReasons(action);
                draft.successEvent = undefined;

                break;
            }

            case CONTRACTING_TIMEOUT_FAILURE: {
                draft.failureEvent = action.type;
                draft.status = 'failure';
                draft.failureCurrentState = undefined;
                draft.failureReasons = [];
                draft.successEvent = undefined;
                break;
            }

            case CONTRACTING_OPEN_MODAL: {
                draft.modalOpen = true;
                draft.stage = undefined;
                break;
            }

            case CONTRACTING_CLOSE_MODAL: {
                draft.modalOpen = false;
                draft.isInProgress = false;
                draft.correlationId = undefined;
                draft.startTimeMs = undefined;
                draft.cancelled = false;
                // Clear FS-specific state
                draft.waitingForFsResponse = false;
                draft.fsEventReceivedTimeMs = undefined;
                draft.fsEventDelayMs = undefined;
                // Reset copy-credit-app session state. All three fields must
                // reset together — leaving any one set across modal sessions
                // corrupts the next attempt's gate evaluation.
                draft.copyStage = 'idle';
                draft.copyAttempts = 0;
                draft.copyInFlightSince = null;
                draft.lastStageTimestampMs = undefined;
                break;
            }

            // Copy-credit-app state machine. Transitions:
            //   AWAITING_CANCEL -> awaiting_cancel/attempts+1/null
            //   REQUEST         -> requesting/_/Date.now()
            //   SUCCESS         -> success/_/null
            //   FAILURE         -> failure/_/null
            // The attempts increment fires on AWAITING_CANCEL (NOT REQUEST) so
            // the one-copy-per-session middleware gate holds even if cancel
            // succeeds but the Cancelled handler never fires the copy. The
            // `copyInFlightSince` timestamp is set on REQUEST so the stall timer
            // is keyed on the post-cancel/pre-ws-event window — setting it on
            // AWAITING_CANCEL would arm the timer too early.
            case CONTRACTING_STAGE_COPY_AWAITING_CANCEL: {
                draft.copyStage = 'awaiting_cancel';
                draft.copyAttempts = state.copyAttempts + 1;
                draft.copyInFlightSince = null;
                draft.lastStageTimestampMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_COPY_REQUEST: {
                draft.copyStage = 'requesting';
                draft.copyInFlightSince = Date.now();
                draft.lastStageTimestampMs = Date.now();
                break;
            }

            // Intermediate state between ws-receipt and the Deal API write
            // settling. Fired by the updateDealForCopyCreditApp RSAA's REQUEST.
            case COPY_CREDIT_APP_DEAL_UPDATE_REQUEST: {
                draft.copyStage = 'updating_deal';
                draft.lastStageTimestampMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_COPY_SUCCESS: {
                draft.copyStage = 'success';
                draft.copyInFlightSince = null;
                draft.lastStageTimestampMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_COPY_FAILURE: {
                draft.copyStage = 'failure';
                draft.copyInFlightSince = null;
                draft.lastStageTimestampMs = Date.now();
                break;
            }

            case CONTRACTING_IN_PROGRESS: {
                draft.isInProgress = true;
                draft.correlationId = nanoid();
                if (state.startTimeMs === undefined) {
                    draft.startTimeMs = Date.now();
                }
                break;
            }

            case CONTRACTING_STAGE_CANCEL_REQUEST: {
                draft.stage = 'cancel';
                draft.cancelRequestStartTimeMs = Date.now();
                draft.correlationId = action.meta.correlationId;
                break;
            }

            case CONTRACTING_STAGE_START_REQUEST: {
                draft.stage = 'start';
                draft.startRequestStartTimeMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_VERIFY_REQUEST: {
                draft.stage = 'verify';
                draft.verifyRequestStartTimeMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_CANCEL_FAILURE:
            case CONTRACTING_STAGE_CANCEL_SUCCESS: {
                draft.cancelRequestEndTimeMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_START_FAILURE:
            case CONTRACTING_STAGE_START_SUCCESS: {
                draft.startRequestEndTimeMs = Date.now();
                break;
            }

            case CONTRACTING_STAGE_VERIFY_FAILURE:
            case CONTRACTING_STAGE_VERIFY_SUCCESS: {
                draft.verifyRequestEndTimeMs = Date.now();
                break;
            }

            case CONTRACTING_WAITING_FOR_FS: {
                draft.waitingForFsResponse = true;
                draft.stage = 'waitingForFs';
                break;
            }

            case CONTRACTING_FS_RESPONSE_RECEIVED: {
                draft.waitingForFsResponse = false;
                draft.fsEventReceivedTimeMs = action.payload.timeMs;
                if (state.saveOfferRequestEndTimeMs) {
                    draft.fsEventDelayMs = action.payload.timeMs - state.saveOfferRequestEndTimeMs;
                }
                break;
            }

            case SET_CONTRACT_VALIDATION_ERRORS: {
                draft.fieldValidationErrors = action.payload ? flattenLeaf(action.payload, 'errorMessage') : [];
                draft.fieldValidationErrorFlags = action.payload ? flattenLeaf(action.payload, 'hasError') : {};
                break;
            }

            case SET_CONTRACT_VALIDATION_LOADING: {
                draft.contractValidationLoading = Boolean(action.payload?.isLoading);
                break;
            }

            case SET_FIRST_CONTRACT_API_ERRORED_VPP: {
                draft.firstContractApiErroredVPP = action.payload;
                break;
            }

            case CONTRACTING_GENERATE_CLICKED: {
                draft.failureCurrentState = undefined;
                draft.failureEvent = undefined;
                draft.failureReasons = undefined;
                draft.status = undefined;
                draft.successEvent = undefined;
                draft.stage = undefined;
                draft.fieldValidationErrors = undefined;
                draft.fieldValidationErrorFlags = undefined;
                draft.firstContractApiErroredVPP = undefined;
                draft.formContractType = undefined;
                break;
            }

            case CONTRACTING_SET_FORM_CONTRACT_TYPE: {
                draft.formContractType = action.payload?.formContractType;
                break;
            }

            case CONTRACTING_EVENT_RECEIVED: {
                const eventName = action.payload?.eventName;
                draft.eventName = eventName;
                draft.eventPayload = action.payload?.payload;

                switch (eventName) {
                    case 'Contracting:Cancelled':
                        draft.cancelEventReceivedTimeMs = Date.now();
                        draft.cancelEventToEventBrokerDelayMs = action.payload.eventToEventBrokerDelayMs;
                        draft.cancelEventBrokerReceivedTimeMs = action.payload.eventBrokerReceivedTimeMs;
                        draft.cancelEventTimeMs = parseEventTime(action.payload.eventTime);
                        draft.cancelEventTimeStamp = action.payload.eventTime;
                        draft.cancelled = true;
                        break;
                    case 'Contracting:CancelFailed': {
                        draft.cancelEventReceivedTimeMs = Date.now();
                        draft.cancelEventToEventBrokerDelayMs = action.payload.eventToEventBrokerDelayMs;
                        draft.cancelEventBrokerReceivedTimeMs = action.payload.eventBrokerReceivedTimeMs;
                        draft.cancelEventTimeMs = parseEventTime(action.payload.eventTime);
                        draft.cancelEventTimeStamp = action.payload.eventTime;
                        draft.cancelled = true;
                        break;
                    }

                    case 'Contracting:SavedSuccessfully':
                    case 'Contracting:SaveFailed': {
                        draft.startEventReceivedTimeMs = Date.now();
                        draft.startEventToEventBrokerDelayMs = action.payload.eventToEventBrokerDelayMs;
                        draft.startEventBrokerReceivedTimeMs = action.payload.eventBrokerReceivedTimeMs;
                        draft.startEventTimeMs = parseEventTime(action.payload.eventTime);
                        draft.startEventTimeStamp = action.payload.eventTime;
                        break;
                    }

                    case 'ContractingDecisions:VerificationFailed':
                    case 'ContractingDecisions:Verified':
                    case 'ContractingDecisions:VerifiedWithWarnings': {
                        draft.verifyEventReceivedTimeMs = Date.now();
                        draft.verifyEventToEventBrokerDelayMs = action.payload.eventToEventBrokerDelayMs;
                        draft.verifyEventBrokerReceivedTimeMs = action.payload.eventBrokerReceivedTimeMs;
                        draft.verifyEventTimeMs = parseEventTime(action.payload.eventTime);
                        draft.verifyEventTimeStamp = action.payload.eventTime;
                        break;
                    }
                }
                break;
            }

            case CONTRACTING_STATUS_SUCCESS: {
                if (action.payload) {
                    draft.contractingStatusInfo = action.payload;
                    draft.contractingStatusFailure = undefined;
                }
                break;
            }

            case CONTRACTING_STATUS_FAILURE: {
                if (action.payload) {
                    const { response } = action.payload as ContractingStatusFailureResponse;
                    draft.contractingStatusFailure = response;
                    draft.contractingStatusInfo = undefined;
                }
                break;
            }
        }
    });
